import { useEffect } from "react";

import config from "@/config";
import fetchWrapper from "@/services/fetchWrapper.service";
import { applySourceToArtworkList, resolveArtworkViewSource } from "@/shared/lib/artworkViewSource";

import {
  appendOffsetToEndpoint,
  mergeUniqueArtworks,
  resolveArtworkSlug,
} from "./artworkFeedState.utils";
import {
  appendSuarteworldSegment,
  shouldAppendSuarteworldSegment,
} from "./suarteworldContinuation";

export default function useArtworkSlideController({
  activeSlide,
  artworks,
  shouldLoadMore,
  location,
  logRouting,
  loadMoreLoading,
  nextLoadMoreOffsetRef,
  exhaustedLoadMoreEndpointsRef,
  setArtworks,
  cacheHandler,
  listCacheKeyRef,
  cacheInvalidationActions,
  suarteworldSegmentStatusRef,
  slug,
  previousSlugRef,
  routeSyncCandidateRef,
  resolveHoldingContext,
  holdingId,
  holdingOwnerId,
  holdingTypeParam,
  requestedViewParam,
  replaceLastDirectorytoNavigate,
}) {
  useEffect(() => {
    logRouting("load-more-decision", {
      shouldLoadMore,
      activeSlide,
      listLength: artworks?.data?.length ?? 0,
      totalDocs: artworks?.totalDocs ?? 0,
      hasEndpoint: Boolean(artworks?.endpoint),
      loadMoreInFlight: loadMoreLoading.current,
    });

    if (
      shouldLoadMore
      && (activeSlide >= (artworks.data.length - 3))
      && !loadMoreLoading.current
    ) {
      if (nextLoadMoreOffsetRef.current === null || nextLoadMoreOffsetRef.current === undefined) {
        nextLoadMoreOffsetRef.current = artworks.data.length;
      }

      const requestOffset = nextLoadMoreOffsetRef.current;
      const loadMoreEndpoint = appendOffsetToEndpoint(artworks.endpoint, requestOffset);
      if (exhaustedLoadMoreEndpointsRef.current.has(loadMoreEndpoint)) {
        logRouting("load-more-skip-exhausted-endpoint", {
          requestEndpoint: loadMoreEndpoint,
          requestOffset,
        });
        return;
      }

      loadMoreLoading.current = true;
      logRouting("load-more-request", {
        endpoint: artworks.endpoint,
        requestEndpoint: loadMoreEndpoint,
        requestOffset,
      });

      fetchWrapper.get(`${config.apis.api.url}${loadMoreEndpoint}`, {
        injectToken: true,
      })
        .then(({ response, data }) => {
          loadMoreLoading.current = false;

          if (response.ok) {
            const incomingList = Array.isArray(data)
              ? data
              : Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data?.data?.data)
                  ? data.data.data
                  : [];
            const responseCount = incomingList.length;
            let dataToStore = null;
            let addedCount = 0;
            setArtworks((prev) => {
              if (!prev || prev.endpoint !== artworks.endpoint) {
                logRouting("load-more-stale-response-ignored", {
                  requestEndpoint: loadMoreEndpoint,
                  requestFeedEndpoint: artworks.endpoint,
                  currentFeedEndpoint: prev?.endpoint ?? null,
                });
                return prev;
              }

              const currentSource = resolveArtworkViewSource({
                itemSource: prev.data?.[0]?._artworkViewSource,
                navigationSource: location.state?.artworkViewSource,
                pathname: location.pathname,
              });
              const incomingData = applySourceToArtworkList(incomingList, currentSource);
              const mergedData = mergeUniqueArtworks(prev.data, incomingData);
              addedCount = Math.max(mergedData.length - prev.data.length, 0);

              dataToStore = { ...prev, totalDocs: data.totalDocs, data: mergedData };
              return dataToStore;
            });

            nextLoadMoreOffsetRef.current = requestOffset + responseCount;
            if (addedCount === 0 || responseCount === 0) {
              exhaustedLoadMoreEndpointsRef.current.add(loadMoreEndpoint);
            }

            if (addedCount === 0 && responseCount === 0) {
              setArtworks((prev) => {
                if (!prev) return prev;
                return { ...prev, totalDocs: prev.data.length };
              });
            }

            logRouting("load-more-merge-result", {
              requestEndpoint: loadMoreEndpoint,
              requestOffset,
              responseCount,
              addedCount,
              nextOffset: nextLoadMoreOffsetRef.current,
            });

            if (dataToStore) {
              const keyToStore = listCacheKeyRef.current || location.state?.cacheKey;
              if (keyToStore) {
                cacheHandler.storeInCache(keyToStore, dataToStore, {
                  invalidateWhen: cacheInvalidationActions,
                });
              }
            }
          } else if (response.status === 404) {
            logRouting("load-more-404-stop", {
              endpoint: loadMoreEndpoint,
              currentLength: artworks.data.length,
            });
            setArtworks((prev) => {
              if (!prev) return prev;
              return { ...prev, totalDocs: prev.data.length };
            });
          } else {
            logRouting("load-more-non-ok", {
              status: response.status,
              endpoint: loadMoreEndpoint,
            });
          }
        })
        .catch(() => {
          loadMoreLoading.current = false;
          logRouting("load-more-exception", { endpoint: loadMoreEndpoint });
        });
    }

    const feedSource = resolveArtworkViewSource({
      itemSource: artworks?.data?.[0]?._artworkViewSource,
      navigationSource: location.state?.artworkViewSource,
      pathname: location.pathname,
    });
    const shouldAppendSuarteworld = shouldAppendSuarteworldSegment({
      feedSource,
      artworksLength: artworks?.data?.length ?? 0,
      totalDocs: artworks?.totalDocs ?? 0,
      activeSlide,
      segmentStatus: suarteworldSegmentStatusRef.current,
    });

    logRouting("suarteworld-segment-decision", {
      feedSourceType: feedSource?.type ?? null,
      feedContinuation: feedSource?.continuation ?? null,
      artworksLength: artworks?.data?.length ?? 0,
      totalDocs: artworks?.totalDocs ?? 0,
      activeSlide,
      segmentStatus: suarteworldSegmentStatusRef.current,
      shouldAppendSuarteworld,
    });

    if (shouldAppendSuarteworld) {
      suarteworldSegmentStatusRef.current = "loading";
      appendSuarteworldSegment({
        fetchWrapper,
        expectedFeedEndpoint: artworks?.endpoint,
        currentArtworks: artworks,
        setArtworks,
        applySourceToArtworkList,
        mergeUniqueArtworks,
        cacheHandler,
        listCacheKey: listCacheKeyRef.current,
        locationCacheKey: location.state?.cacheKey,
        logRouting,
      })
        .then((didAppend) => {
          suarteworldSegmentStatusRef.current = didAppend ? "idle" : "exhausted";
          logRouting("suarteworld-segment-status-update", {
            didAppend,
            nextStatus: suarteworldSegmentStatusRef.current,
          });
        })
        .catch(() => {
          suarteworldSegmentStatusRef.current = "idle";
          logRouting("suarteworld-segment-exception");
        });
    }

    const confirmRouteSyncCandidate = (signature, payload = {}) => {
      if (!signature) {
        routeSyncCandidateRef.current = null;
        return true;
      }

      if (routeSyncCandidateRef.current !== signature) {
        routeSyncCandidateRef.current = signature;
        logRouting("route-sync-candidate-armed", {
          signature,
          ...payload,
        });
        return false;
      }

      routeSyncCandidateRef.current = null;
      return true;
    };

    if (artworks) {
      const routeChangeInFlight = previousSlugRef.current !== slug;
      if (routeChangeInFlight) {
        logRouting("skip-route-sync", {
          reason: "route-change-in-flight",
          previousSlug: previousSlugRef.current,
          nextSlug: slug,
          activeSlide,
        });
        return;
      }

      const activeArtwork = artworks.data[activeSlide];
      if (!activeArtwork) {
        routeSyncCandidateRef.current = null;
        logRouting("skip-route-sync", {
          reason: "active-artwork-not-found",
          activeSlide,
          listLength: artworks.data.length,
        });
        return;
      }
      const activeArtworkSlug = resolveArtworkSlug(
        activeArtwork,
        activeArtwork?.product_id ?? activeArtwork?._id,
      );
      const activeHoldingContext = resolveHoldingContext(
        activeArtwork,
        holdingId,
        holdingOwnerId,
        holdingTypeParam,
      );
      const hasActiveHolding =
        Boolean(activeHoldingContext.holdingId)
        && Boolean(activeHoldingContext.ownerId);

      if (activeArtworkSlug && slug !== activeArtworkSlug) {
        const routeSyncSignature = hasActiveHolding
          ? [
            "holding-mismatch",
            activeArtworkSlug,
            activeHoldingContext.holdingId,
            activeHoldingContext.ownerId,
            activeHoldingContext.holdingType,
            activeSlide,
          ].join("::")
          : [
            "slug-mismatch",
            activeArtworkSlug,
            activeSlide,
          ].join("::");

        logRouting("route-sync-mismatch-detected", {
          currentSlug: slug,
          activeArtworkSlug,
          hasActiveHolding,
        });

        const shouldCommitRouteSync = hasActiveHolding
          ? confirmRouteSyncCandidate(routeSyncSignature, {
            reason: "holding-mismatch",
            currentSlug: slug,
            nextSlug: activeArtworkSlug,
            activeSlide,
          })
          : true;

        if (!shouldCommitRouteSync) {
          return;
        }

        if (hasActiveHolding) {
          const params = new URLSearchParams();
          params.set("view", "holding");
          params.set("holding_id", activeHoldingContext.holdingId);
          params.set("owner_id", activeHoldingContext.ownerId);
          if (activeHoldingContext.holdingType) {
            params.set("holding_type", activeHoldingContext.holdingType);
          }
          replaceLastDirectorytoNavigate(
            activeArtworkSlug,
            params,
            { type: "holding", ...activeHoldingContext },
            "active-slide-slug-mismatch-with-holding",
          );
        } else {
          routeSyncCandidateRef.current = null;
          replaceLastDirectorytoNavigate(activeArtworkSlug, null, null, "active-slide-slug-mismatch");
        }
      } else if (activeArtworkSlug) {
        if (hasActiveHolding) {
          const queryDifferent =
            holdingId !== activeHoldingContext.holdingId
            || (activeHoldingContext.ownerId && activeHoldingContext.ownerId !== holdingOwnerId)
            || (activeHoldingContext.holdingType && activeHoldingContext.holdingType !== holdingTypeParam)
            || requestedViewParam !== "holding";

          if (queryDifferent) {
            const routeSyncSignature = [
              "holding-query-diff",
              activeArtworkSlug,
              activeHoldingContext.holdingId,
              activeHoldingContext.ownerId,
              activeHoldingContext.holdingType,
              activeSlide,
            ].join("::");
            logRouting("route-sync-holding-query-diff", {
              activeArtworkSlug,
              currentHoldingId: holdingId,
              nextHoldingId: activeHoldingContext.holdingId,
            });
            const shouldCommitRouteSync = confirmRouteSyncCandidate(routeSyncSignature, {
              reason: "holding-query-diff",
              activeArtworkSlug,
              currentHoldingId: holdingId,
              nextHoldingId: activeHoldingContext.holdingId,
              activeSlide,
            });
            if (!shouldCommitRouteSync) {
              return;
            }
            const params = new URLSearchParams();
            params.set("view", "holding");
            params.set("holding_id", activeHoldingContext.holdingId);
            params.set("owner_id", activeHoldingContext.ownerId);
            if (activeHoldingContext.holdingType) {
              params.set("holding_type", activeHoldingContext.holdingType);
            }
            replaceLastDirectorytoNavigate(
              activeArtworkSlug,
              params,
              { type: "holding", ...activeHoldingContext },
              "holding-query-sync",
            );
          } else {
            routeSyncCandidateRef.current = null;
          }
        } else if (holdingId || requestedViewParam === "holding" || holdingOwnerId) {
          routeSyncCandidateRef.current = null;
          logRouting("route-sync-clear-holding", {
            activeArtworkSlug,
            holdingId,
            holdingOwnerId,
            requestedViewParam,
          });
          replaceLastDirectorytoNavigate(activeArtworkSlug, null, null, "clear-holding-query");
        } else {
          routeSyncCandidateRef.current = null;
        }
      }
    }
  }, [
    activeSlide,
    artworks,
    cacheHandler,
    cacheInvalidationActions,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    listCacheKeyRef,
    loadMoreLoading,
    location,
    logRouting,
    nextLoadMoreOffsetRef,
    previousSlugRef,
    replaceLastDirectorytoNavigate,
    requestedViewParam,
    resolveHoldingContext,
    routeSyncCandidateRef,
    setArtworks,
    shouldLoadMore,
    slug,
    suarteworldSegmentStatusRef,
    exhaustedLoadMoreEndpointsRef,
  ]);
}
